import {
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
  useState,
} from 'react';
import { useTransition, animated, easings } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { Play } from '@hivebrite/design-system/Play';

interface VideoPlayerProps {
  file?: File;
  height?: string;
  isLoading?: boolean;
  url?: string;
  width?: string;
}

const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  ({ url, file, width = '100%', height = 'auto', isLoading }, ref) => {
    const playerRef = useRef<HTMLVideoElement>(null);
    const blobUrl = useMemo(
      () => (file ? URL.createObjectURL(file) : url),
      [file, url],
    );

    const [isPlaying, setIsPlaying] = useState(false);

    const transitions = useTransition(isPlaying, {
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 },
      config: {
        duration: 200,
        easings: easings.easeInOutQuart,
      },
    });

    const handlePlay = () => {
      if (!isPlaying && playerRef.current?.paused) {
        playerRef.current?.play();
        setIsPlaying(true);
      }
    };

    const handlePause = () => {
      if (playerRef.current?.seeking) {
        return;
      }
      setIsPlaying(false);
    };

    // Through a video element to forwardRef.
    useImperativeHandle(ref, () => playerRef.current as HTMLVideoElement);

    return (
      <Wrapper width={width} height={height} data-testid="video-player">
        {!isLoading ? (
          <>
            <video
              ref={playerRef}
              src={blobUrl}
              controls={isPlaying}
              width={width}
              height={height}
              onPause={handlePause}
              preload="auto"
              style={{ position: 'relative' }}
            />
            {transitions((style, isPlaying) =>
              isPlaying ? null : (
                <AnimatedDimmer
                  onClick={handlePlay}
                  style={{ opacity: style.opacity }}
                >
                  <Play ariaLabel={__('Start the video preview')} />
                </AnimatedDimmer>
              ),
            )}
          </>
        ) : (
          <WrapperLoader>
            <Loader size={72} />
          </WrapperLoader>
        )}
      </Wrapper>
    );
  },
);

export default VideoPlayer;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  bg: 'cloud',
  color: 'dove',
  justifyContent: 'center',
  maxHeight: '100%',
}))`
  overflow: hidden;
  position: relative;
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
`;

const AnimatedDimmer = styled(animated(Box)).attrs<any>(() => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}))`
  background-color: rgba(0, 0, 0, 0.3);
`;

const WrapperLoader = styled(Box)`
  width: 100%;
  aspect-ratio: 16/9;
`;
